import {
  makeStyles,
  Typography,
  Table,
  TableBody,
  TableRow,
} from "@material-ui/core"
import dayjs from "dayjs"
import { useParams } from "react-router-dom"
import { useMediaQuery } from "react-responsive"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import {
  QuestionnaireTakesSinceDateByUserIdDocument,
  QuestionnaireTakesSinceDateByUserIdQuery,
  QuestionnaireTakesSinceDateByUserIdQueryVariables,
  useGetRiskAlertLogByIdQuery,
} from "../../generated/graphql"
import Logo from "../atoms/Logo"
import { colors } from "../../utils/theme"
import { HeaderHeight } from "../molecules/Header"
import TableCell from "../atoms/TableCell"
import RiskAssessmentQuestionnaireChart from "../molecules/RiskAssessmentQuestionnaireChart"
import { useEffect, useState } from "react"
import { ChartDataPoints, QuestionnaireResponseProps } from "../../utils/types"
import {
  getRiskAssessmentChartLines,
  getRiskAssessmentQuestion,
  getRiskAssessmentResponseValue,
} from "../../utils/helpers"

const useStyles = makeStyles((theme) => ({
  header: {
    backgroundColor: "#EEF5FD",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    height: HeaderHeight,
    position: "fixed",
    zIndex: 1000,
    width: "100%",
  },
  logo: {
    margin: 0,
    padding: 0,
    border: 0,
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
  title: {
    color: colors.neutral.denim,
  },
  alertDetail: {
    paddingTop: 121,
    backgroundColor: colors.neutral.white,
    flex: 1,
    paddingLeft: 52,
    paddingRight: 52,
  },
  subTitle: {
    color: colors.neutral.denim,
    paddingTop: 10,
    fontWeight: 400,
  },
  summaries: {
    marginTop: theme.spacing(3),
  },
  summary: {
    display: "flex",
    alignItems: "center",
  },
  summaryName: {
    height: 25,
    width: 159,
    fontWeight: 700,
  },
  summaryValue: {
    color: colors.neutral.denim,
    fontWeight: 400,
  },
  rest: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    // width: "100%",
  },
  responses: {
    marginTop: 25,
    maxWidth: 384,
  },
  response: {
    borderBottom: `1px solid ${colors.neutral.clay}`,
    backgroundColor: colors.neutral.white,
    maxHeight: 90,
    color: `${colors.neutral.denim} !important`,
    paddingBottom: 10,
  },
  alertResponse: {
    borderBottom: `1px solid ${colors.neutral.clay}`,
    maxHeight: 90,
    color: `${colors.neutral.denim} !important`,
    paddingBottom: 10,
    backgroundColor: `${colors.secondary.crimson}20`,
  },
  responseHead: {
    borderBottom: `1px solid ${colors.neutral.clay}`,
    backgroundColor: colors.neutral.white,
    maxHeight: 60,
    color: `${colors.neutral.denim} !important`,
    "& .MuiTypography-root": {
      fontWeight: "bold",
      display: "inline",
    },
    paddingBottom: 10,
  },
  table: {
    color: `${colors.neutral.denim} !important`,
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${colors.neutral.clay}`,
    backgroundColor: colors.neutral.white,
    maxWidth: 1058,
    marginTop: 25,
  },
  headRow: {
    height: 58,
    "& .MuiTypography-root": {
      fontWeight: "bold",
      display: "inline",
    },
  },
  bodyRow: {
    height: 58,
  },
  alertRow: {
    height: 58,
    backgroundColor: `${colors.secondary.crimson}20`,
  },
  bodyCell: {
    width: 709,
  },
  chartContainer: {
    borderRadius: 4,
    border: `1px solid ${colors.neutral.clay}`,
    padding: theme.spacing(3),
    maxWidth: 400,
    height: 365,
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  chartDateRange: {
    fontStyle: "italic",
    fontWeight: 400,
    color: colors.neutral.denim,
    opacity: 0.8,
  },
  chartLastResultDate: {
    fontWeight: 400,
    color: colors.neutral.denim,
    opacity: 0.8,
  },
  chartTitle: {
    fontWeight: 400,
    color: colors.neutral.denim,
    marginBottom: 4,
  },
  chartsFlexContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    flexDirection: "row",
    marginTop: theme.spacing(3),
  },
}))

interface Params {
  id: string
}

interface Chart {
  name: string
  id: number
  lastResultDate: string
  data: ChartDataPoints[]
}

const RiskAlertViewer = () => {
  const { id } = useParams<Params>()
  const graphQLClient = useGraphQLClient()
  const classes = useStyles()

  const isDesktopOrLaptop = useMediaQuery({ minWidth: 1224 })
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 })

  const { data } = useGetRiskAlertLogByIdQuery(graphQLClient, {
    id,
  })

  const questionnaireTake =
    data?.risk_alerts_log[0]?.risk_alerts_log_questionnaire_take ?? null

  const clientId =
    data?.risk_alerts_log[0]?.risk_alerts_log_questionnaire_take?.user.id ??
    null

  const [charts, setCharts] = useState<Chart[]>([])

  const [riskAssessmentQuestion, setRiskAssessmentQuestion] =
    useState<string>("")

  const [chartLines, setChartLines] = useState<string[]>([])

  useEffect(() => {
    const fetchQuestionnaireTakes = async () => {
      if (clientId) {
        try {
          const recentQuestionnaires = await graphQLClient.request<
            QuestionnaireTakesSinceDateByUserIdQuery,
            QuestionnaireTakesSinceDateByUserIdQueryVariables
          >(QuestionnaireTakesSinceDateByUserIdDocument, {
            userId: clientId,
            sinceDate: dayjs().subtract(6, "months").toISOString(),
          })

          const chartsByType = new Map<
            number,
            { name: string; data: ChartDataPoints[] }
          >()

          if (recentQuestionnaires.questionnaire_takes.length !== 0) {
            recentQuestionnaires.questionnaire_takes.forEach((take) => {
              const hasRiskQuestions =
                take.questionnaire_schedule?.questionnaire
                  ?.contains_risk_questions ?? false

              const questionnaireResponses =
                take.questionnaire_responses as QuestionnaireResponseProps[]

              const status = take.completion_status

              if (hasRiskQuestions && status === "complete") {
                const riskAssessmentQuestion = getRiskAssessmentQuestion(
                  questionnaireResponses,
                  1
                )
                setRiskAssessmentQuestion(riskAssessmentQuestion)
                const chartLinesValues = getRiskAssessmentChartLines(1)
                setChartLines(chartLinesValues)
                let y = getRiskAssessmentResponseValue(
                  questionnaireResponses,
                  1
                )
                const x = take.completed_at
                const date = dayjs(take.completed_at).format("MMM D, YYYY")
                const name =
                  take.questionnaire_schedule?.questionnaire.name ?? ""

                const id = take.questionnaire_schedule?.questionnaire.id!
                const color =
                  take.questionnaireDisplayValues?.scoring_display_color ??
                  "grey"
                const other =
                  take.questionnaireDisplayValues?.scoring_display_name ??
                  undefined
                if (!chartsByType.has(id)) {
                  chartsByType.set(id, { name: name, data: [] })
                }
                const chart = chartsByType.get(id)! // we just set this above
                chart.data.push({
                  x,
                  y,
                  stroke: color,
                  fill: `${color}80`, // make it 50% opaque
                  date,
                  label: "",
                  other,
                })
                chartsByType.set(id, chart)
              }
            })

            const charts = Array.from(chartsByType.keys()).map((key) => {
              // get the last 8 sorted by date
              const data = chartsByType
                .get(key)!
                .data.sort((a, b) => (a.x > b.x ? 1 : -1))
                .slice(-8)

              return {
                id: key,
                data,
                name: chartsByType.get(key)!.name,
                lastResultDate: data[data.length - 1].x,
              }
            })
            setCharts(charts)
          }
        } catch (error) {}
      }
    }
    fetchQuestionnaireTakes()
  }, [clientId])

  return (
    <>
      <header className={classes.header}>
        <button className={classes.logo}>
          <Logo />
        </button>
      </header>
      <div className={classes.alertDetail}>
        <Typography variant="h2" className={classes.title}>
          Risk Alert
        </Typography>
        <Typography variant="body1" className={classes.subTitle}>
          Vira has detected a response outside the risk threshold. Please
          contact this client as soon as possible.
        </Typography>
        <div className={classes.summaries}>
          <div className={classes.summary}>
            <Typography variant="body1" className={classes.summaryName}>
              Name
            </Typography>
            <div className={classes.rest}>
              <Typography variant="body1" className={classes.summaryValue}>
                {questionnaireTake?.user.first_name}{" "}
                {questionnaireTake?.user.last_name}
              </Typography>
            </div>
          </div>
          <div className={classes.summary}>
            <Typography variant="body1" className={classes.summaryName}>
              Email
            </Typography>
            <div className={classes.rest}>
              <Typography variant="body1" className={classes.summaryValue}>
                {questionnaireTake?.user.display_name}
              </Typography>
            </div>
          </div>
          <div className={classes.summary}>
            <Typography variant="body1" className={classes.summaryName}>
              Assessment
            </Typography>
            <div className={classes.rest}>
              <Typography variant="body1" className={classes.summaryValue}>
                {questionnaireTake?.questionnaire_schedule.questionnaire.name}
              </Typography>
            </div>
          </div>
          <div className={classes.summary}>
            <Typography variant="body1" className={classes.summaryName}>
              Completed at
            </Typography>
            <div className={classes.rest}>
              <Typography variant="body1" className={classes.summaryValue}>
                {dayjs(questionnaireTake?.completed_at).format(
                  "MM/DD/YYYY h:mm A"
                )}{" "}
                PT
              </Typography>
            </div>
          </div>
        </div>
        {isDesktopOrLaptop && (
          <Table className={classes.table}>
            <TableBody>
              <TableRow className={classes.headRow}>
                <TableCell colSpan={2}>
                  <Typography variant="body1">Question</Typography>{" "}
                  {questionnaireTake?.questionnaire_responses[0]
                    .questionnaire_item.header ?? ""}
                </TableCell>
              </TableRow>
              {questionnaireTake?.questionnaire_responses.map((response) => (
                <>
                  <TableRow
                    className={
                      response.questionnaire_response_options
                        ?.triggers_risk_alert
                        ? classes.alertRow
                        : classes.bodyRow
                    }
                  >
                    <TableCell className={classes.bodyCell}>
                      {response.questionnaire_item.question}
                    </TableCell>
                    <TableCell>
                      {response.questionnaire_response_options?.response_text}
                    </TableCell>
                  </TableRow>
                </>
              ))}
            </TableBody>
          </Table>
        )}
        {isTabletOrMobile && (
          <div className={classes.responses}>
            <div className={classes.responseHead}>
              <Typography variant="body1">Question</Typography>{" "}
              {questionnaireTake?.questionnaire_responses[0].questionnaire_item
                .header ?? "123"}
            </div>
            {questionnaireTake?.questionnaire_responses.map((response) => (
              <div
                className={
                  response.questionnaire_response_options?.triggers_risk_alert
                    ? classes.alertResponse
                    : classes.response
                }
              >
                <Typography variant="body1">
                  {response.questionnaire_item.question}
                </Typography>
                <Typography variant="body1">
                  {response.questionnaire_response_options?.response_text}
                </Typography>
              </div>
            ))}
          </div>
        )}

        <div className={classes.chartsFlexContainer}>
          {charts.map((chart) => (
            <div key={chart.name} className={classes.chartContainer}>
              <div className={classes.chartHeader}>
                <div>
                  <Typography variant="h5" className={classes.chartTitle}>
                    {chart.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={classes.chartLastResultDate}
                  >
                    Last Result:{" "}
                    {dayjs(chart.lastResultDate).format("MMM DD, YYYY")}
                  </Typography>
                </div>
                <Typography
                  variant="caption"
                  className={classes.chartDateRange}
                >
                  Last {chart.data.length} questionnaires
                </Typography>
              </div>
              <div>
                <Typography variant="caption">
                  {riskAssessmentQuestion}
                </Typography>
              </div>
              <RiskAssessmentQuestionnaireChart
                data={chart.data}
                id={chart.id}
                chartLines={chartLines}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default RiskAlertViewer
